// Pure (no DOM) decisions about how a chat message should render beyond its
// base text. Shared by the live post-response path and the session-
// rehydration path (project switch, "New chat" reload) so the two can't
// silently drift apart — that drift is exactly what caused rich content
// (download button, ribbon-sync note, attached-file chip) to disappear on
// rehydration: the metadata was persisted correctly server-side, but the
// rehydration renderer only ever read `message.content` (plain text).

/**
 * Given a message's metadata, decide what download control (if any) it
 * should carry. Returns null when there's nothing to show. The button says
 * `label` (a calendar change sends "Download calendar XML").
 * @param {Object|null|undefined} metadata
 * @returns {{downloadUrl: string, fileId: (string|undefined), fileName: string, label: string, bridgeSyncCommandId: (string|null)}|null}
 */
export function extractDownloadInfo(metadata) {
  if (!metadata) return null;
  let downloadUrl = null;
  let fileId = null;
  let fileName = "project.xml";
  let label = "Download XML";

  if (metadata.file_metadata) {
    downloadUrl = metadata.file_metadata.download_url || metadata.download_url || null;
    fileId = metadata.file_metadata.file_id;
    fileName = metadata.file_metadata.filename || fileName;
    label = metadata.file_metadata.label || label;
  }
  if (!downloadUrl && metadata.download_url) {
    downloadUrl = metadata.download_url;
  }
  if (!downloadUrl) return null;

  return {
    downloadUrl,
    fileId,
    fileName,
    label,
    bridgeSyncCommandId: metadata?.bridge_sync?.command_id || null,
  };
}

/**
 * The notice for a sync the backend refused because the plan this chat started
 * from was read before the last sync -- preparing a rebuild from it would have
 * undone that sync (see backend services/sync_freshness.py). Null otherwise.
 * @param {Object|null|undefined} metadata
 * @returns {string|null}
 */
export function staleSyncNotice(metadata) {
  const sync = metadata?.bridge_sync;
  if (!sync || sync.status !== "stale_context") return null;
  const text = sync.message || "This change was not prepared because the plan was still being read. Send your request again in a few seconds.";
  return `⚠ ${text}`;
}

/**
 * True for an edit the backend found to change nothing, so it staged nothing
 * (bridge_sync.status "no_changes"). The reply already says there is nothing
 * to sync; the pane must not add "Ribbon Sync is unavailable, use Download
 * XML", which reads as a failure.
 * @param {Object|null|undefined} metadata
 * @returns {boolean}
 */
export function isNoChangesSync(metadata) {
  return metadata?.bridge_sync?.status === "no_changes";
}

/**
 * Given a message's metadata, decide what change-summary block (if any) it
 * should carry (GID-95). Returns null when there were no tool calls to
 * summarize -- never an empty box.
 * @param {Object|null|undefined} metadata
 * @returns {{groups: Array, hasFailures: boolean}|null}
 */
export function extractToolCallSummary(metadata) {
  // An edit that changed nothing has no changes to summarise: the box lists
  // calls that ran, and "Updated: 2 hammocks" under "Nothing in this edit
  // changes your plan" contradicted the reply (GID-286).
  if (isNoChangesSync(metadata)) {
    return null;
  }
  const summary = metadata?.tool_call_summary;
  if (!summary || !Array.isArray(summary.groups) || summary.groups.length === 0) {
    return null;
  }
  return {
    groups: summary.groups,
    hasFailures: Boolean(summary.has_failures),
  };
}

/**
 * Given a stored message (from a live response OR rehydrated session
 * history), decide how it should render beyond its base text.
 * @param {Object} msg - has at least { id, message_type, metadata }
 * @returns {{kind: "hidden"}
 *          |{kind: "attached_file", messageId: *, filename: *, size: *, wasSummarised: *}
 *          |{kind: "text", downloadInfo: (Object|null), toolCallSummary: (Object|null)}}
 */
export function classifyMessageForRender(msg) {
  if (msg?.metadata?.ui_hidden === true) {
    return { kind: "hidden" };
  }
  if (msg?.message_type === "attached_file") {
    return {
      kind: "attached_file",
      messageId: msg.id,
      filename: msg.metadata?.filename,
      size: msg.metadata?.size,
      wasSummarised: msg.metadata?.was_summarised,
    };
  }
  return {
    kind: "text",
    downloadInfo: extractDownloadInfo(msg?.metadata),
    toolCallSummary: extractToolCallSummary(msg?.metadata),
  };
}
